/**
 * The stored content overrides (`GET /content`, contract §5) and the three writes on
 * them. `blocks` is keyed by content key; a key absent from it renders its default.
 *
 * Every successful write refreshes `blocks` from the server's answer and calls
 * `invalidateSiteContent()`, so the public landing page reads the edit on its next render.
 * Writes REJECT with the axios error on failure — the caller owns the message
 * (`siteErrorMessage`), because only it knows which field to attach it to.
 *
 * @param {{ api?: typeof siteApi }} [options]
 * @returns {{
 *   status: "loading" | "ready" | "error", blocks: Record<string, object>, error: unknown,
 *   reload: () => Promise<void>,
 *   saveText: (key: string, values: {value_fr: string, value_en: string, value_es: string}) => Promise<object>,
 *   saveImage: (key: string, mediaId: string) => Promise<object>,
 *   reset: (key: string) => Promise<void>,
 * }}
 */
import { useCallback, useEffect, useState } from "react";
import siteApi from "./siteApi";
import { invalidateSiteContent } from "../../lib/siteContentInvalidation";

const byKey = (items) => Object.fromEntries((Array.isArray(items) ? items : []).map((b) => [b.key, b]));

export default function useSiteContentAdmin({ api = siteApi } = {}) {
  const [status, setStatus] = useState("loading");
  const [blocks, setBlocks] = useState({});
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setStatus("loading");
    try {
      const body = await api.listContent();
      setBlocks(byKey(body?.items));
      setError(null);
      setStatus("ready");
    } catch (e) {
      setError(e);
      setStatus("error");
    }
  }, [api]);

  useEffect(() => { reload(); }, [reload]);

  const stored = useCallback((block) => {
    setBlocks((prev) => ({ ...prev, [block.key]: block }));
    invalidateSiteContent();
    return block;
  }, []);

  const saveText = useCallback(
    async (key, values) => stored(await api.putText(key, values)),
    [api, stored],
  );

  const saveImage = useCallback(
    async (key, mediaId) => stored(await api.putImage(key, mediaId)),
    [api, stored],
  );

  const reset = useCallback(async (key) => {
    await api.resetContent(key);
    setBlocks((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    invalidateSiteContent();
  }, [api]);

  return { status, blocks, error, reload, saveText, saveImage, reset };
}
